import {useMemo} from 'react'

import {useBrand} from '../_web/brand/hooks/useBrand'
import {BrandMessage} from '../brand/types'
import {
  applyRoundingStrategy,
  RoundingStrategyVariants,
  toDecreasePercentage
} from '../utils/number'

const SAVING_THRESHOLD = 5
const ALLOWED_CURRENCIES_TO_SHOW_PRICE = [
  'EUR',
  'USD',
  'GBP',
  'AUD',
  'CHF',
  'KWD',
  'SGD',
  'NZD',
  'BHD',
  'CAD'
]
export type VioTagSavingType = 'percentage' | 'number'

interface Props {
  price: number
  anchorPrice: number
  currencyCode: string
  isVioOffer?: boolean
}

export interface VioTagDetails {
  savingPrice: number
  savingPercentage: number
  isVioTagVisible: boolean
  savingType: 'number' | 'percentage'
  tagMessage: BrandMessage
}

/**
 * A hook to calculate the Vio tag details.
 * @param param - The parameters object
 * @param param.price - The price of the offer.
 * @param param.anchorPrice - The anchor price of the offer.
 * @param param.currencyCode - The currency code of the offer.
 * @param param.isVioOffer - Whether the offer is a Vio offer.
 * @returns The Vio tag details.
 */
export const useVioTag = ({
  price = 0,
  anchorPrice = 0,
  currencyCode = '',
  isVioOffer = true
}: Props): VioTagDetails => {
  const {brandOnlyOnBrandTagMessage, brandEnabledOnlyOnBrandTag} = useBrand()
  const roundedPrice =
    applyRoundingStrategy(RoundingStrategyVariants.round, price) ?? 0
  const roundedAnchorPrice =
    applyRoundingStrategy(RoundingStrategyVariants.round, anchorPrice) ?? 0
  const savingPercentage =
    toDecreasePercentage(
      roundedPrice,
      roundedAnchorPrice,
      RoundingStrategyVariants.floor
    ) ?? 0

  const isVioTagVisible: boolean = useMemo(
    () =>
      savingPercentage > SAVING_THRESHOLD &&
      brandEnabledOnlyOnBrandTag &&
      isVioOffer,
    [savingPercentage, isVioOffer]
  )

  const savingType: VioTagSavingType = useMemo(() => {
    if (
      savingPercentage > SAVING_THRESHOLD &&
      ALLOWED_CURRENCIES_TO_SHOW_PRICE.includes(currencyCode)
    )
      return 'number'
    return 'percentage'
  }, [savingPercentage, currencyCode])

  return {
    savingPrice: roundedAnchorPrice - roundedPrice,
    savingPercentage,
    isVioTagVisible,
    savingType,
    tagMessage: brandOnlyOnBrandTagMessage
  }
}
